/**
 * 사이트 다국어(#315)의 언어 경로 규칙. 서버·클라이언트·미들웨어가 함께 쓰는 순수 함수만 둔다.
 *
 * 주소 체계(추천 A, 사용자 답 대기 중): 한국어는 지금 주소 그대로, 영어만 `/en` 접두어.
 * 체계를 B(`/ko` 도 접두어)로 바꾸게 되면 이 파일의 `DEFAULT_PREFIXED` 와 두 함수만 고치면 된다.
 * 문서(/docs)는 따로 `/docs/{ko|en}/{버전}` 체계를 쓰므로 여기서 다루지 않는다.
 */
export const LOCALES = ['ko', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'ko';

/** true 면 기본 언어(ko)도 `/ko` 접두어를 쓴다(주소 체계 B). */
export const DEFAULT_PREFIXED = false;

/** 선택한 언어를 기억하는 쿠키. 자동 이동에는 쓰지 않고 안내 배너와 전환 단추 표시에만 쓴다. */
export const LOCALE_COOKIE = 'bareun-locale';
/** 미들웨어가 요청 헤더에 실어 서버 컴포넌트에 넘기는 값. */
export const LOCALE_HEADER = 'x-bareun-locale';
export const PATH_HEADER = 'x-bareun-path';

/** 언어 접두어를 붙이지 않는 경로(언어와 무관하거나 자체 언어 체계가 있음). */
const UNLOCALIZED_PREFIXES = ['/api', '/docs', '/_next', '/admin'];

export function isLocale(value: string | null | undefined): value is Locale {
  return !!value && (LOCALES as readonly string[]).includes(value);
}

function isUnlocalized(path: string): boolean {
  return UNLOCALIZED_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
}

/** `/en/product` → `{ locale: 'en', rest: '/product' }`. 접두어가 없으면 기본 언어. */
export function splitLocale(pathname: string): { locale: Locale; rest: string } {
  const m = /^\/([a-z]{2})(\/.*)?$/.exec(pathname);
  if (m && isLocale(m[1]) && (m[1] !== DEFAULT_LOCALE || DEFAULT_PREFIXED)) {
    return { locale: m[1], rest: m[2] || '/' };
  }
  if (DEFAULT_PREFIXED && m && m[1] === DEFAULT_LOCALE) {
    return { locale: DEFAULT_LOCALE, rest: m[2] || '/' };
  }
  return { locale: DEFAULT_LOCALE, rest: pathname || '/' };
}

/** 언어 없는 경로(rest)에 언어 접두어를 붙인다. 문서·API 같은 경로는 그대로 둔다. */
export function localizePath(rest: string, locale: Locale): string {
  const path = rest.startsWith('/') ? rest : `/${rest}`;
  if (isUnlocalized(path)) return path;
  if (locale === DEFAULT_LOCALE && !DEFAULT_PREFIXED) return path;
  return path === '/' ? `/${locale}` : `/${locale}${path}`;
}

/**
 * 영어 화면이 준비된 경로(번역이 끝난 화면).
 * 여기 없는 영어 주소는 noindex 로 두고 canonical 을 한국어로 보낸다(번역이 빈 화면이 색인되지 않게).
 * 화면을 번역할 때마다 여기에 접두어를 더한다. 비어 있으면 hreflang 도 나가지 않는다.
 */
export const EN_READY_PREFIXES: string[] = [
  '/home',
  '/product',
  '/correct',
  '/analyze',
  '/tokenize',
  '/docs/en'
];

/** 접두어 아래에 있어도 아직 번역하지 않은 화면 */
export const EN_NOT_READY: string[] = ['/product/billing-guide'];

export function hasEnglish(rest: string): boolean {
  if (EN_NOT_READY.includes(rest.replace(/\/$/, ''))) return false;
  return EN_READY_PREFIXES.some((p) =>
    p === '/' ? rest === '/' : rest === p || rest.startsWith(p + '/')
  );
}

/** hreflang 에 쓸 언어별 경로. 영어가 없는 화면이면 null. */
export function alternateLanguages(rest: string): Record<string, string> | null {
  if (!hasEnglish(rest)) return null;
  return {
    ko: localizePath(rest, 'ko'),
    en: localizePath(rest, 'en'),
    'x-default': localizePath(rest, 'ko')
  };
}

/**
 * 화면(page)이 metadata.alternates 로 쓸 값. canonical 은 그 언어의 자기 주소이고,
 * 영어가 준비된 화면이면 hreflang 도 함께 낸다. 영어가 없는 화면의 영어 주소는 canonical 을 한국어로 보낸다.
 * (alternates 는 자식이 선언하면 부모 값을 통째로 덮으므로, canonical 을 선언하는 화면은 이 값을 써야 hreflang 이 남는다.)
 */
export function alternatesFor(rest: string, locale: Locale) {
  const languages = alternateLanguages(rest);
  const self = locale === 'en' && !hasEnglish(rest) ? 'ko' : locale;
  return {
    canonical: localizePath(rest, self),
    ...(languages && { languages })
  };
}
